import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Mic, Eye, ShieldCheck, ShieldAlert, Calendar, Clock, X, Check, FileEdit, FolderArchive, Handshake, GaugeCircle } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import EvidenceTab from '@/components/talentfix/EvidenceTab';
import MediationTab from '@/components/talentfix/MediationTab';
import SufficiencyTab from '@/components/talentfix/SufficiencyTab';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import i18next from 'i18next';

const L = (en, es) => ((i18next.language || 'es').startsWith('es') ? es : en);

const STATUS_TONE = {
  scheduled: 'bg-blue-100 text-blue-700',
  planned: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
  no_show: 'bg-rose-100 text-rose-700',
};

const fmt = (s) => (s ? String(s).slice(0, 16).replace('T', ' ') : '—');

// ---------------------------------------------------------------------
// Schedule Interview Modal
// ---------------------------------------------------------------------
function ScheduleInterviewDialog({ open, onOpenChange, caseId, onCreated }) {
  const [form, setForm] = useState({
    kind: 'consultant_interview',
    subject_external_label: '',
    subject_display_name: '',
    date_time: '',
    duration_minutes: '60',
    format: 'video',
  });
  const [submitting, setSubmitting] = useState(false);
  const reset = () => setForm({ kind: 'consultant_interview', subject_external_label: '', subject_display_name: '', date_time: '', duration_minutes: '60', format: 'video' });

  const submit = async () => {
    if (!form.subject_external_label.trim()) {
      toast.error(L('Subject label is required','La etiqueta del sujeto es obligatoria'));
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        kind: form.kind,
        subject_external_label: form.subject_external_label.trim(),
        subject_display_name: form.subject_display_name.trim() || null,
        date_time: form.date_time || null,
        duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
        format: form.format,
      };
      await adminApi.talentFixScheduleInterview(caseId, body);
      toast.success(L('Interview scheduled','Entrevista programada'));
      reset(); onOpenChange(false); onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Schedule failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf3-schedule-interview-dialog">
        <DialogHeader>
          <DialogTitle>{L('Schedule interview','Programar entrevista')}</DialogTitle>
          <DialogDescription>{L('Subject can be a platform user or an external participant (label only).','El sujeto puede ser un usuario de la plataforma o un participante externo (solo etiqueta).')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">{L('Kind','Tipo')}</label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="tf3-int-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="consultant_interview">{L('Consultant interview (1:1 with subject)','Entrevista del consultor (1:1 con el sujeto)')}</SelectItem>
                <SelectItem value="stakeholder_interview">{L('Stakeholder interview (third-party)','Entrevista a interesado (tercero)')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Subject label *</label>
            <Input
              data-testid="tf3-int-subject"
              value={form.subject_external_label}
              onChange={(e) => setForm({ ...form, subject_external_label: e.target.value })}
              placeholder={L('e.g. Pablo Restrepo — Ops Lead','p. ej. Pablo Restrepo — Líder de Operaciones')}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">{L('Display name (optional)','Nombre visible (opcional)')}</label>
            <Input
              value={form.subject_display_name}
              onChange={(e) => setForm({ ...form, subject_display_name: e.target.value })}
              placeholder={L('What appears on the case timeline','Lo que aparece en la línea de tiempo del caso')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">{L('Date / time','Fecha / hora')}</label>
              <Input
                type="datetime-local"
                value={form.date_time ? form.date_time.slice(0, 16) : ''}
                onChange={(e) => setForm({ ...form, date_time: e.target.value ? `${e.target.value}:00Z` : '' })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">{L('Duration (min)','Duración (min)')}</label>
              <Input type="number" min="5" max="1440" value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}/>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">{L('Format','Formato')}</label>
            <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="video">{L('Video','Video')}</SelectItem>
                <SelectItem value="in_person">{L('In person','Presencial')}</SelectItem>
                <SelectItem value="phone">{L('Phone','Teléfono')}</SelectItem>
                <SelectItem value="async_written">{L('Async / written','Asíncrona / escrita')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf3-int-submit">
            {submitting ? 'Scheduling…' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Plan Observation Modal
// ---------------------------------------------------------------------
function PlanObservationDialog({ open, onOpenChange, caseId, onCreated }) {
  const [form, setForm] = useState({ kind: 'live_meeting', target_label: '', date_time: '' });
  const [submitting, setSubmitting] = useState(false);
  const reset = () => setForm({ kind: 'live_meeting', target_label: '', date_time: '' });

  const submit = async () => {
    if (!form.target_label.trim() || form.target_label.trim().length < 2) {
      toast.error(L('Target label is required (2+ chars)','La etiqueta del objetivo es obligatoria (2+ caracteres)'));
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        kind: form.kind,
        target_label: form.target_label.trim(),
        date_time: form.date_time || null,
      };
      await adminApi.talentFixPlanObservation(caseId, body);
      toast.success(L('Observation planned','Observación planificada'));
      reset(); onOpenChange(false); onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Plan failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf3-plan-observation-dialog">
        <DialogHeader>
          <DialogTitle>{L('Plan observation','Planificar observación')}</DialogTitle>
          <DialogDescription>
            Note: async log reviews require consent capture at completion time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">{L('Kind','Tipo')}</label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="tf3-obs-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="live_meeting">{L('Live meeting','Reunión en vivo')}</SelectItem>
                <SelectItem value="one_on_one">1:1 (live)</SelectItem>
                <SelectItem value="standup">{L('Standup / huddle','Standup / reunión breve')}</SelectItem>
                <SelectItem value="shadow_day">{L('Shadow day','Día de acompañamiento')}</SelectItem>
                <SelectItem value="async_log_review">{L('Async log review (requires consent)','Revisión asíncrona de registros (requiere consentimiento)')}</SelectItem>
                <SelectItem value="work_product_review">{L('Work product review','Revisión de productos de trabajo')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Target label *</label>
            <Input
              data-testid="tf3-obs-target"
              value={form.target_label}
              onChange={(e) => setForm({ ...form, target_label: e.target.value })}
              placeholder={L('e.g. Weekly Ops/Sales sync','p. ej. Sincronización semanal Operaciones/Ventas')}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">{L('Date / time','Fecha / hora')}</label>
            <Input
              type="datetime-local"
              value={form.date_time ? form.date_time.slice(0, 16) : ''}
              onChange={(e) => setForm({ ...form, date_time: e.target.value ? `${e.target.value}:00Z` : '' })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf3-obs-submit">
            {submitting ? 'Planning…' : 'Plan observation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Complete Interview Dialog (5-panel notes)
// ---------------------------------------------------------------------
function CompleteInterviewDialog({ open, onOpenChange, interview, onCompleted }) {
  const [notes, setNotes] = useState({ context: '', facts: '', perceptions: '', quotes: '', advisor_only_impressions: '' });
  const [duration, setDuration] = useState('');
  const [consentMethod, setConsentMethod] = useState('click_through');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setNotes({ context: '', facts: '', perceptions: '', quotes: '', advisor_only_impressions: '' });
      setDuration(interview?.duration_minutes ? String(interview.duration_minutes) : '');
      setConsentMethod('click_through');
    }
  }, [open, interview]);

  const submit = async () => {
    const filled = Object.values(notes).some((v) => (v || '').trim());
    if (!filled) { toast.error(L('Add at least one panel of notes','Agrega al menos un panel de notas')); return; }
    setSubmitting(true);
    try {
      await adminApi.talentFixCompleteInterview(interview.id, {
        notes_structured: notes,
        duration_minutes: duration ? Number(duration) : null,
        consent_capture: {
          method: consentMethod,
          accepted_at: new Date().toISOString(),
          accepted_by_user_id: interview.subject_user_id || 'subject-external',
        },
      });
      toast.success(L('Interview completed','Entrevista completada'));
      onOpenChange(false); onCompleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Complete failed');
    } finally { setSubmitting(false); }
  };

  if (!interview) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="tf3-complete-interview-dialog">
        <DialogHeader>
          <DialogTitle>Complete interview · {interview.subject_external_label || interview.subject_display_name}</DialogTitle>
          <DialogDescription>{L('5-panel structured notes. Submitting locks the interview as completed.','Notas estructuradas en 5 paneles. Al enviar, la entrevista queda bloqueada como completada.')}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3">
          {[
            ['context', 'Context — where / when / who'],
            ['facts', 'Facts — observed events, dates, named actors'],
            ['perceptions', 'Perceptions — subject\'s subjective interpretation'],
            ['quotes', 'Quotes — verbatim memorable phrases'],
            ['advisor_only_impressions', 'Advisor-only impressions (private — NOT shared with client)'],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="text-xs font-medium text-slate-600">{label}</label>
              <Textarea
                data-testid={`tf3-iv-note-${key}`}
                rows={key === 'advisor_only_impressions' ? 3 : 3}
                value={notes[key]}
                onChange={(e) => setNotes({ ...notes, [key]: e.target.value })}
              />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">{L('Actual duration (min)','Duración real (min)')}</label>
              <Input type="number" min="5" max="1440" value={duration} onChange={(e) => setDuration(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">{L('Consent method','Método de consentimiento')}</label>
              <Select value={consentMethod} onValueChange={setConsentMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="click_through">{L('Click-through','Aceptación en pantalla')}</SelectItem>
                  <SelectItem value="voice">{L('Voice (recorded)','Voz (grabada)')}</SelectItem>
                  <SelectItem value="written">{L('Written','Escrito')}</SelectItem>
                  <SelectItem value="implicit_attendance">{L('Implicit (attendance)','Implícito (asistencia)')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf3-iv-complete-submit">
            {submitting ? 'Completing…' : 'Mark completed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Complete Observation Dialog (4-panel notes)
// ---------------------------------------------------------------------
function CompleteObservationDialog({ open, onOpenChange, observation, onCompleted }) {
  const [notes, setNotes] = useState({ setting: '', behaviour: '', interactions: '', advisor_only_impressions: '' });
  const [consentMethod, setConsentMethod] = useState('implicit_attendance');
  const [submitting, setSubmitting] = useState(false);
  const isAsyncReview = observation?.kind === 'async_log_review';

  useEffect(() => {
    if (open) {
      setNotes({ setting: '', behaviour: '', interactions: '', advisor_only_impressions: '' });
      setConsentMethod(isAsyncReview ? 'written' : 'implicit_attendance');
    }
  }, [open, observation, isAsyncReview]);

  const submit = async () => {
    const filled = Object.values(notes).some((v) => (v || '').trim());
    if (!filled) { toast.error(L('Add at least one panel of notes','Agrega al menos un panel de notas')); return; }
    setSubmitting(true);
    try {
      const body = {
        notes_structured: notes,
        ...(isAsyncReview ? {
          consent_capture: {
            method: consentMethod,
            accepted_at: new Date().toISOString(),
            accepted_by_user_id: 'client-hr',
          },
        } : {}),
      };
      await adminApi.talentFixCompleteObservation(observation.id, body);
      toast.success(L('Observation completed','Observación completada'));
      onOpenChange(false); onCompleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Complete failed');
    } finally { setSubmitting(false); }
  };

  if (!observation) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="tf3-complete-observation-dialog">
        <DialogHeader>
          <DialogTitle>Complete observation · {observation.target_label}</DialogTitle>
          <DialogDescription>
            4-panel structured notes.
            {isAsyncReview && (
              <span className="ml-1 inline-flex items-center gap-1 text-amber-700">
                <ShieldAlert className="h-3.5 w-3.5" />Async log review requires consent capture.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3">
          {[
            ['setting', 'Setting — environment, attendees, conditions'],
            ['behaviour', 'Behaviour — what people did'],
            ['interactions', 'Interactions — between whom, what happened'],
            ['advisor_only_impressions', 'Advisor-only impressions (private — NOT shared with client)'],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="text-xs font-medium text-slate-600">{label}</label>
              <Textarea
                data-testid={`tf3-ob-note-${key}`}
                rows={3}
                value={notes[key]}
                onChange={(e) => setNotes({ ...notes, [key]: e.target.value })}
              />
            </div>
          ))}
          {isAsyncReview && (
            <div>
              <label className="text-xs font-medium text-slate-600">Consent method *</label>
              <Select value={consentMethod} onValueChange={setConsentMethod}>
                <SelectTrigger data-testid="tf3-ob-consent-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="written">{L('Written authorization','Autorización escrita')}</SelectItem>
                  <SelectItem value="click_through">{L('Click-through','Aceptación en pantalla')}</SelectItem>
                  <SelectItem value="voice">{L('Voice (recorded)','Voz (grabada)')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf3-ob-complete-submit">
            {submitting ? 'Completing…' : 'Mark completed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Cancel Dialog (shared)
// ---------------------------------------------------------------------
function CancelDialog({ open, onOpenChange, item, kind, onCancelled }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);

  const submit = async () => {
    if (reason.trim().length < 5) { toast.error(L('Reason required (5+ chars)','Motivo requerido (5+ caracteres)')); return; }
    setSubmitting(true);
    try {
      if (kind === 'interview') await adminApi.talentFixCancelInterview(item.id, { reason: reason.trim() });
      else await adminApi.talentFixCancelObservation(item.id, { reason: reason.trim() });
      toast.success(L('Cancelled','Cancelado'));
      onOpenChange(false); onCancelled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    } finally { setSubmitting(false); }
  };

  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`tf3-cancel-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>Cancel {kind}</DialogTitle>
          <DialogDescription>
            {kind === 'interview' ? item.subject_external_label : item.target_label}
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder={L('Why is this being cancelled? (required, 5+ chars)','¿Por qué se cancela? (obligatorio, 5+ caracteres)')}
          data-testid={`tf3-cancel-${kind}-reason`} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Back','Volver')}</Button>
          <Button variant="destructive" onClick={submit} disabled={submitting}
            data-testid={`tf3-cancel-${kind}-submit`}>
            {submitting ? 'Cancelling…' : 'Cancel ' + kind}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Read-only View Notes Dialog (post-completion review surface)
// ---------------------------------------------------------------------
const INTERVIEW_PANELS_RO = [
  ['context', 'Context — where / when / who'],
  ['facts', 'Facts — observed events, dates, named actors'],
  ['perceptions', 'Perceptions — subject\'s subjective interpretation'],
  ['quotes', 'Quotes — verbatim memorable phrases'],
  ['advisor_only_impressions', 'Advisor-only impressions (private — NOT shared with client)'],
];
const OBSERVATION_PANELS_RO = [
  ['setting', 'Setting — physical / temporal context'],
  ['behaviour', 'Behaviour — what was observed'],
  ['interactions', 'Interactions — between people'],
  ['advisor_only_impressions', 'Advisor-only impressions (private — NOT shared with client)'],
];

function ViewNotesDialog({ open, onOpenChange, item, kind }) {
  if (!item) return null;
  const panels = kind === 'interview' ? INTERVIEW_PANELS_RO : OBSERVATION_PANELS_RO;
  const notes = item.notes_structured || {};
  const title = kind === 'interview'
    ? (item.subject_external_label || item.subject_display_name || 'Interview')
    : (item.target_label || 'Observation');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid={`tf3-view-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>{kind === 'interview' ? 'Interview' : 'Observation'} notes · {title}</DialogTitle>
          <DialogDescription>
            Read-only view. Status: <strong className="capitalize">{(item.status || '').replace(/_/g, ' ')}</strong>
            {item.consultant_name && <> · Consultant: {item.consultant_name}</>}
            {item.date_time && <> · {fmt(item.date_time)}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {panels.map(([key, label]) => {
            const value = (notes[key] || '').trim();
            const isPrivate = key === 'advisor_only_impressions';
            return (
              <div key={key}
                   className={isPrivate ? 'rounded-md border border-amber-300 bg-amber-50 p-3' : 'rounded-md border border-slate-200 bg-white p-3'}
                   data-testid={`tf3-view-${kind}-panel-${key}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-slate-700">{label}</p>
                  {isPrivate && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-200 text-amber-900 text-xs font-medium px-2 py-0.5">
                      <ShieldCheck className="h-3 w-3" />Locked from client view
                    </span>
                  )}
                </div>
                <p className="text-sm whitespace-pre-wrap text-slate-800">
                  {value ? value : <span className="italic text-slate-400">— empty —</span>}
                </p>
              </div>
            );
          })}
          {item.consent_capture?.method && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-xs font-medium text-emerald-800 inline-flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" />Consent captured · {String(item.consent_capture.method).replace(/_/g, ' ')}
              </p>
              {item.consent_capture.accepted_at && (
                <p className="text-xs text-emerald-700 mt-1">at {fmt(item.consent_capture.accepted_at)}</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>{L('Close','Cerrar')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// List Card (interview or observation row)
// ---------------------------------------------------------------------
function ItemCard({ item, kind, onComplete, onCancel, onView }) {
  const title = kind === 'interview' ? (item.subject_external_label || item.subject_display_name) : item.target_label;
  const subtitle = kind === 'interview'
    ? `${(item.kind || '').replace(/_/g, ' ')} · ${(item.format || '').replace(/_/g, ' ')}`
    : `${(item.kind || '').replace(/_/g, ' ')}`;
  const isOpen = item.status === 'scheduled' || item.status === 'planned';
  const isCompleted = item.status === 'completed' && item.notes_structured;
  return (
    <Card data-testid={`tf3-${kind}-card-${item.id}`} className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {kind === 'interview' ? <Mic className="h-4 w-4 text-blue-600" /> : <Eye className="h-4 w-4 text-violet-600" />}
            <span className="font-medium text-slate-800">{title}</span>
            <Badge className={STATUS_TONE[item.status] || 'bg-slate-100 text-slate-600'}>
              {(item.status || '').replace(/_/g, ' ')}
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 capitalize">{subtitle}</p>
          {item.date_time && (
            <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />{fmt(item.date_time)}
            </p>
          )}
          {item.consultant_name && (
            <p className="text-xs text-slate-500 mt-0.5">Consultant: {item.consultant_name}</p>
          )}
          {item.status === 'cancelled' && item.cancel_reason && (
            <p className="mt-2 text-xs rounded bg-slate-50 border border-slate-200 px-2 py-1 text-slate-600">
              <span className="font-medium">{L('Cancel reason:','Motivo de cancelación:')}</span> {item.cancel_reason}
            </p>
          )}
          {isCompleted && (
            <div className="mt-2 text-xs rounded bg-emerald-50 border border-emerald-200 px-2 py-1 text-emerald-800">
              <Check className="inline h-3 w-3 mr-1" />Structured notes captured
              {item.consent_capture?.method && (
                <span className="ml-2 inline-flex items-center gap-1 text-emerald-700">
                  <ShieldCheck className="h-3 w-3" />consent: {item.consent_capture.method.replace(/_/g, ' ')}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          {isOpen && (
            <>
              <Button size="sm" onClick={onComplete} data-testid={`tf3-${kind}-complete-${item.id}`}>
                <Check className="h-3.5 w-3.5 mr-1" />Complete
              </Button>
              <Button size="sm" variant="outline" onClick={onCancel} data-testid={`tf3-${kind}-cancel-${item.id}`}>
                <X className="h-3.5 w-3.5 mr-1" />Cancel
              </Button>
            </>
          )}
          {isCompleted && (
            <Button size="sm" variant="outline" onClick={onView}
                    data-testid={`tf3-${kind}-view-${item.id}`}>
              <Eye className="h-3.5 w-3.5 mr-1" />View notes
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------
export default function AdminTalentFixInvestigation() {
  const { id: caseId } = useParams();
  const { user } = useApp();
  const isHmAdmin = user?.role === 'hm_admin';
  const [loading, setLoading] = useState(true);
  const [caseRow, setCaseRow] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [observations, setObservations] = useState([]);
  const [tab, setTab] = useState('interviews');

  // Dialog state
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [planObsOpen, setPlanObsOpen] = useState(false);
  const [completeIv, setCompleteIv] = useState(null);
  const [completeOb, setCompleteOb] = useState(null);
  const [cancelIv, setCancelIv] = useState(null);
  const [cancelOb, setCancelOb] = useState(null);
  const [viewIv, setViewIv] = useState(null);
  const [viewOb, setViewOb] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, dp, iv, ob] = await Promise.all([
        adminApi.talentFixGetCase(caseId),
        adminApi.talentFixGetDiagnosticPlan(caseId).catch(() => ({ plan: null })),
        adminApi.talentFixListInterviews(caseId).catch(() => ({ items: [] })),
        adminApi.talentFixListObservations(caseId).catch(() => ({ items: [] })),
      ]);
      setCaseRow(c);
      setPlanStatus(dp?.plan?.status || null);
      setInterviews(iv.items || []);
      setObservations(ob.items || []);
    } catch (e) {
      toast.error(L('Could not load investigation workspace','No se pudo cargar el espacio de investigación'));
    } finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const stage3Unlocked = planStatus === 'consent_granted';

  const counts = useMemo(() => ({
    iv_scheduled: interviews.filter((i) => i.status === 'scheduled').length,
    iv_completed: interviews.filter((i) => i.status === 'completed').length,
    ob_planned: observations.filter((o) => o.status === 'planned').length,
    ob_completed: observations.filter((o) => o.status === 'completed').length,
  }), [interviews, observations]);

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (!caseRow) return <div className="p-6 text-sm text-rose-600">{L('Case not found.','Caso no encontrado.')}</div>;

  return (
    <div className="space-y-6 p-6" data-testid="tf3-investigation-page">
      <div>
        <Link to={`/admin/talentfix/cases/${caseId}/diagnostic-plan`}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" />Back to Diagnostic Plan
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">Investigation Workspace · Stage 3</h1>
        <p className="text-sm text-slate-500">{caseRow.title} · {caseRow.company_name}</p>
      </div>

      {!stage3Unlocked && (
        <Card className="border-amber-200 bg-amber-50" data-testid="tf3-locked-card">
          <CardHeader>
            <CardTitle className="text-base inline-flex items-center gap-1.5 text-amber-800">
              <ShieldAlert className="h-4 w-4" />Stage 3 is locked
            </CardTitle>
            <CardDescription className="text-amber-700">
              Stage 3 interviews and observations unlock only after the Client Admin approves the Diagnostic Plan.
              Current plan status: <code>{planStatus || 'not_started'}</code>.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <Card className="p-3"><p className="text-slate-500">{L('Interviews scheduled','Entrevistas programadas')}</p><p className="text-xl font-semibold" data-testid="tf3-stat-iv-scheduled">{counts.iv_scheduled}</p></Card>
        <Card className="p-3"><p className="text-slate-500">{L('Interviews completed','Entrevistas completadas')}</p><p className="text-xl font-semibold" data-testid="tf3-stat-iv-completed">{counts.iv_completed}</p></Card>
        <Card className="p-3"><p className="text-slate-500">{L('Observations planned','Observaciones planificadas')}</p><p className="text-xl font-semibold" data-testid="tf3-stat-ob-planned">{counts.ob_planned}</p></Card>
        <Card className="p-3"><p className="text-slate-500">{L('Observations completed','Observaciones completadas')}</p><p className="text-xl font-semibold" data-testid="tf3-stat-ob-completed">{counts.ob_completed}</p></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="interviews" data-testid="tf3-tab-interviews">
            <Mic className="h-3.5 w-3.5 mr-1" />Interviews ({interviews.length})
          </TabsTrigger>
          <TabsTrigger value="observations" data-testid="tf3-tab-observations">
            <Eye className="h-3.5 w-3.5 mr-1" />Observations ({observations.length})
          </TabsTrigger>
          <TabsTrigger value="evidence" data-testid="tf4-tab-evidence">
            <FolderArchive className="h-3.5 w-3.5 mr-1" />Evidence Vault
          </TabsTrigger>
          <TabsTrigger value="mediations" data-testid="tf5-tab-mediations">
            <Handshake className="h-3.5 w-3.5 mr-1" />Mediations
          </TabsTrigger>
          <TabsTrigger value="sufficiency" data-testid="tf6-sufficiency-tab">
            <GaugeCircle className="h-3.5 w-3.5 mr-1" />Sufficiency
          </TabsTrigger>
        </TabsList>

        <TabsContent value="interviews" className="space-y-3 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setScheduleOpen(true)} disabled={!stage3Unlocked}
                    data-testid="tf3-schedule-interview-btn">
              <Plus className="h-4 w-4 mr-1.5" />Schedule interview
            </Button>
          </div>
          {interviews.length === 0
            ? <p className="text-sm italic text-slate-500">{L('No interviews yet.','Aún no hay entrevistas.')}</p>
            : interviews.map((i) => (
                <ItemCard key={i.id} item={i} kind="interview"
                  onComplete={() => setCompleteIv(i)}
                  onCancel={() => setCancelIv(i)}
                  onView={() => setViewIv(i)} />
              ))}
        </TabsContent>

        <TabsContent value="observations" className="space-y-3 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setPlanObsOpen(true)} disabled={!stage3Unlocked}
                    data-testid="tf3-plan-observation-btn">
              <Plus className="h-4 w-4 mr-1.5" />Plan observation
            </Button>
          </div>
          {observations.length === 0
            ? <p className="text-sm italic text-slate-500">{L('No observations yet.','Aún no hay observaciones.')}</p>
            : observations.map((o) => (
                <ItemCard key={o.id} item={o} kind="observation"
                  onComplete={() => setCompleteOb(o)}
                  onCancel={() => setCancelOb(o)}
                  onView={() => setViewOb(o)} />
              ))}
        </TabsContent>

        <TabsContent value="evidence" className="mt-4">
          <EvidenceTab caseId={caseId} isHmAdmin={isHmAdmin} planStatus={planStatus} />
        </TabsContent>

        <TabsContent value="mediations" className="mt-4">
          <MediationTab caseId={caseId} planStatus={planStatus} />
        </TabsContent>

        <TabsContent value="sufficiency" className="mt-4">
          <SufficiencyTab
            caseId={caseId}
            companyId={caseRow.company_id}
            isHmAdmin={isHmAdmin}
            currentUserId={user?.id}
          />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ScheduleInterviewDialog open={scheduleOpen} onOpenChange={setScheduleOpen}
        caseId={caseId} onCreated={load} />
      <PlanObservationDialog open={planObsOpen} onOpenChange={setPlanObsOpen}
        caseId={caseId} onCreated={load} />
      <CompleteInterviewDialog open={!!completeIv} onOpenChange={(o) => !o && setCompleteIv(null)}
        interview={completeIv} onCompleted={() => { setCompleteIv(null); load(); }} />
      <CompleteObservationDialog open={!!completeOb} onOpenChange={(o) => !o && setCompleteOb(null)}
        observation={completeOb} onCompleted={() => { setCompleteOb(null); load(); }} />
      <CancelDialog open={!!cancelIv} onOpenChange={(o) => !o && setCancelIv(null)}
        item={cancelIv} kind="interview" onCancelled={() => { setCancelIv(null); load(); }} />
      <CancelDialog open={!!cancelOb} onOpenChange={(o) => !o && setCancelOb(null)}
        item={cancelOb} kind="observation" onCancelled={() => { setCancelOb(null); load(); }} />
      <ViewNotesDialog open={!!viewIv} onOpenChange={(o) => !o && setViewIv(null)}
        item={viewIv} kind="interview" />
      <ViewNotesDialog open={!!viewOb} onOpenChange={(o) => !o && setViewOb(null)}
        item={viewOb} kind="observation" />
    </div>
  );
}
